import React, { useState } from "react";
import {
  Box,
  Button,
  Divider,
  Paper,
  Stack,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import { Link, useHistory, useLocation } from "react-router-dom";
import { useAuth } from "../auth/AuthProvider";

export default function AuthLogin() {
  const { login, continueFree, status, error } = useAuth();
  const history = useHistory();
  const location = useLocation();
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const [form, setForm] = useState({ identifier: "", password: "" });

  const handleSubmit = async (event) => {
    event.preventDefault();
    const result = await login({
      login: form.identifier,
      password: form.password,
    });
    if (!result.ok) return;

    const from = location.state?.from || "";
    const target =
      result.role === "admin"
        ? from.startsWith("/admin")
          ? from
          : "/admin"
        : from && !from.startsWith("/admin")
        ? from
        : "/market-data";

    history.push(target);
  };

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr", md: "1.2fr 0.8fr" },
        gap: 3,
        alignItems: "center",
      }}
    >
      <Paper elevation={0} sx={{ p: 3, borderRadius: 3 }}>
        <Stack spacing={2.5}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: 2,
                display: "grid",
                placeItems: "center",
                background: isDark
                  ? "rgba(56,189,248,0.2)"
                  : "rgba(37,99,235,0.12)",
              }}
            >
              <LockOutlinedIcon />
            </Box>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 800 }}>
                Sign in to Passivest
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.7 }}>
                Member and admin access is secured by role.
              </Typography>
            </Box>
          </Stack>

          <form onSubmit={handleSubmit}>
            <Stack spacing={2}>
              <TextField
                label="Email, phone, or username"
                value={form.identifier}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, identifier: e.target.value }))
                }
                fullWidth
              />
              <TextField
                label="Password"
                type="password"
                value={form.password}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, password: e.target.value }))
                }
                fullWidth
              />
              {error ? (
                <Typography variant="body2" sx={{ color: "error.main" }}>
                  {error}
                </Typography>
              ) : null}
              <Button type="submit" variant="contained" size="large" disabled={status === "loading"}>
                Continue
              </Button>
            </Stack>
          </form>

          <Divider />
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
            <Button
              component={Link}
              to="/register"
              variant="outlined"
              sx={{ borderRadius: 999 }}
            >
              Create member account
            </Button>
            <Button
              variant="text"
              sx={{ borderRadius: 999 }}
              onClick={() => {
                continueFree();
                history.push("/");
              }}
            >
              Continue as free explorer
            </Button>
          </Stack>
        </Stack>
      </Paper>

      <Paper
        elevation={0}
        sx={{
          p: 3,
          borderRadius: 3,
          background: isDark
            ? "linear-gradient(160deg, rgba(30,41,59,0.9), rgba(15,23,42,0.7))"
            : "linear-gradient(160deg, rgba(248,250,252,0.9), #ffffff)",
        }}
      >
        <Stack spacing={2}>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            What you get when signed in
          </Typography>
          {[
            "Personalized market health checks",
            "Goal-based rebalancing suggestions",
            "Instant alerts for volatility shifts",
            "Priority support and learning tracks",
          ].map((item) => (
            <Stack key={item} direction="row" spacing={1} alignItems="center">
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  background: "#2563eb",
                }}
              />
              <Typography variant="body2">{item}</Typography>
            </Stack>
          ))}
        </Stack>
      </Paper>
    </Box>
  );
}
